import {
  Card,
  CardBody,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  FormGroup,
} from "reactstrap";
import "moment-timezone";
import { useState, useEffect, useContext } from "react";
import {
  NavLink as DomNavLink,
  useHistory,
  useLocation,
} from "react-router-dom";
import LoadingOverlay from "react-loading-overlay";
import { MainContext } from "context/MainContext.js";
LoadingOverlay.propTypes = undefined;

import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import ChartComponent from "components/Utils/Chart";
import Switch from "react-bootstrap-switch";

const EndpointSpectrum = () => {
  const history = useHistory();
  const location = useLocation();

  const [params, setParams] = useState(location.state?.value ?? "");
  const privileges = useContext(MainContext);
  const emptyValues = [{ labels: 0, powerDBs: 0 }];

  const [details, setDetails] = useState();

  const [current, setCurrent] = useState(true);
  const [t0, setT0] = useState(false);
  const [t4, setT4] = useState(false);

  const PSD_NOISE_OFFSET = 48.75;

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["devices.endpoints.details.display"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
    fetchData();
    loadData();
  }, []);

  const fetchData = () => {
    fetch("/api/v1/endpoint/spectrum/details", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
        macAddress: params,
      },
    })
      .then((response) => response.json())
      .then((actualData) => {
        setDetails(actualData.data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const loadInProgress = () => {
    if (
      upstreamSNRStatus == "loading" ||
      downstreamSNRStatus == "loading" ||
      upstreamPSDStatus == "loading" ||
      downstreamPSDStatus == "loading" ||
      upstreamNoiseStatus == "loading" ||
      downstreamNoiseStatus == "loading"
    ) {
      return true;
    }

    return false;
  };

  const loadData = async () => {
    await manageData("upstream", "snrProbe");
    await manageData("downstream", "snrProbe");
    await manageData("upstream", "psdRx");
    await manageData("downstream", "psdRx");
    await manageData("upstream", "noise");
    await manageData("downstream", "noise");
  };

  const refresh = () => {
    setUpstreamSNRStatus("loading");
    setDownstreamSNRStatus("loading");
    setUpstreamPSDStatus("loading");
    setDownstreamPSDStatus("loading");
    setUpstreamNoiseStatus("loading");
    setDownstreamNoiseStatus("loading");

    loadData();
  };

  const showOverlayMessage = (status) => {
    if (status == "loading") {
      return "Loading Data";
    } else {
      return "";
    }
  };

  const fetchSpectrum = async (direction, type) => {
    try {
      const response = await fetch(`/api/v1/endpoint/spectrum`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          sessionId: sessionStorage.getItem("sessionId"),
          macAddress: params,
          direction: direction,
          type: type,
          averaging: "avg4",
          current: current,
          t0: t0,
          t4: t4,
        },
      });
      return await response.json();
    } catch (err) {
      console.log(err.message);
    }
  };

  const getLineColor = (item, index) => {
    if (item.tier == null) {
      if (index == 0) {
        return "rgb(76, 212, 88)";
      } else {
        return "rgb(212, 203, 76)";
      }
    } else if (item.tier == 0) {
      return "rgb(0, 0, 0)";
    } else if (item.tier > 0) {
      return "rgb(72, 93, 218)";
    }
  };

  const getLineWidth = (item) => {
    if (item.tier == null) {
      return 1.5;
    } else if (item.tier == 0) {
      return 5;
    } else if (item.tier > 0) {
      return 1.5;
    }
  };

  const getLineOrder = (item) => {
    if (item.tier == null) {
      return 0;
    } else if (item.tier == 0) {
      return 10;
    } else if (item.tier > 0) {
      return 5;
    }
  };

  const manageData = async (direction, type) => {
    let hasError = false;
    let result = null;

    try {
      result = await fetchSpectrum(direction, type);
      hasError = result.status != 200;
    } catch (error) {
      hasError = true;
    }

    // Mapping
    const dataSetters = {
      upstream: {
        snrProbe: [
          setUpstreamSNR,
          setUpstreamSNRStatus,
          setUpstreamSNRMaxPowerDb,
          setUpstreamSNRDataSet,
        ],
        psdRx: [
          setUpstreamPSDData,
          setUpstreamPSDStatus,
          setUpstreamPSDMaxPowerDb,
          setUpstreamPSDDataSet,
        ],
        noise: [
          setUpstreamNoiseData,
          setUpstreamNoiseStatus,
          setUpstreamNoiseMaxPowerDb,
          setUpstreamNoiseDataSet,
        ],
      },
      downstream: {
        snrProbe: [
          setDownstreamSNR,
          setDownstreamSNRStatus,
          setDownstreamSNRMaxPowerDb,
          setDownstreamSNRDataSet,
        ],
        psdRx: [
          setDownstreamPSDData,
          setDownstreamPSDStatus,
          setDownstreamPSDMaxPowerDb,
          setDownstreamPSDDataSet,
        ],
        noise: [
          setDownstreamNoiseData,
          setDownstreamNoiseStatus,
          setDownstreamNoiseMaxPowerDb,
          setDownstreamNoiseDataSet,
        ],
      },
    };

    if (dataSetters[direction] && dataSetters[direction][type]) {
      const [setData, setStatus, setMaxPowerDB, setDataSet] =
        dataSetters[direction][type];

      if (hasError) {
        setData(emptyValues);
        setMaxPowerDB(null);
        setStatus("ok");
      } else {
        setData(result.data);
        setMaxPowerDB(result.data[0]?.maxPowerDB);
        setStatus("ok");

        if (type === "snrProbe") {
          const seen = new Set();
          const agcValues = result.data
            .filter((item) => {
              if (seen.has(item.tier)) return false;
              seen.add(item.tier);
              return true;
            })
            .map((item) => ({
              tier: item.tier,
              label: getTierLabel(item.tier),
              value: item.agcDb,
            }));

          if (direction === "upstream") setUpstreamAgcDB(agcValues);
          if (direction === "downstream") setDownstreamAgcDB(agcValues);
        }

        const yAxisConfig = getYAxisConfig(type);

        setDataSet({
          data: {
            labels: result.data[0]?.labels || [],
            datasets: result.data.map((item, index) => ({
              borderColor: getLineColor(item, index),
              pointRadius: 0,
              fill: true,
              borderWidth: getLineWidth(item),
              data: transformPowerDBs(item?.powerDBs || [], type),
              order: getLineOrder(item),
            })),
          },
          options: {
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: true,
              },
            },
            scales: {
              y: {
                min: yAxisConfig.min,
                max: yAxisConfig.max,
                title: {
                  display: true,
                  text: yAxisConfig.label,
                },
              },
              x: {
                type: "linear",
                title: { display: true, text: "MHz" },
                ticks: {
                  callback: function (value) {
                    return parseFloat(value).toFixed(0);
                  },
                },
              },
            },
            animation: false,
          },
        });
      }
    }
  };

  const getAgcColor = (tier) => {
    if (tier === null)
      return "linear-gradient(to right, rgb(76, 212, 88), rgb(212, 203, 76))";
    if (tier === 0) return "rgb(0, 0, 0)";
    return "rgb(72, 93, 218)";
  };

  const getYAxisConfig = (type) => {
    if (type === "noise" || type === "psdRx") {
      return { label: "dBmV (1 Hz)", min: -120, max: -20 };
    }
    return { label: "dB", min: 0, max: 60 };
  };

  const getTierLabel = (tier) => {
    if (tier === null) return "Current";
    if (tier === 0) return "T0";
    if (tier === 1) return "T1";
    if (tier === 2) return "T2";
    if (tier === 3) return "T3";
    if (tier === 4) return "T4";
    return "";
  };

  const transformPowerDBs = (powerDBs, type) => {
    if (type === "noise" || type === "psdRx") {
      return powerDBs.map((value) => value + PSD_NOISE_OFFSET);
    }
    return powerDBs;
  };

  // AGC DB
  const [upstreamAgcDB, setUpstreamAgcDB] = useState([]);
  const [downstreamAgcDB, setDownstreamAgcDB] = useState([]);

  // SNR
  const [upstreamSNRStatus, setUpstreamSNRStatus] = useState("loading");
  const [upstreamSNR, setUpstreamSNR] = useState(emptyValues);
  const [upstreamSNRMaxPowerDb, setUpstreamSNRMaxPowerDb] = useState(null);
  const [downstreamSNRStatus, setDownstreamSNRStatus] = useState("loading");
  const [downstreamSNR, setDownstreamSNR] = useState(emptyValues);
  const [downstreamSNRMaxPowerDb, setDownstreamSNRMaxPowerDb] = useState(null);
  const [upstreamSNRDataSet, setUpstreamSNRDataSet] = useState({
    data: {
      labels: upstreamSNR.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamSNR.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dB",
            min: 0,
            max: 60,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });
  const [downstreamSNRDataSet, setDownstreamSNRDataSet] = useState({
    data: {
      labels: downstreamSNR.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamSNR.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dB",
            min: 0,
            max: 60,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  // PSD
  const [upstreamPSDStatus, setUpstreamPSDStatus] = useState("loading");
  const [upstreamPSDData, setUpstreamPSDData] = useState(emptyValues);
  const [upstreamPSDMaxPowerDb, setUpstreamPSDMaxPowerDb] = useState(null);

  const [downstreamPSDStatus, setDownstreamPSDStatus] = useState("loading");
  const [downstreamPSDData, setDownstreamPSDData] = useState(emptyValues);
  const [downstreamPSDMaxPowerDb, setDownstreamPSDMaxPowerDb] = useState(null);

  const [upstreamPSDDataSet, setUpstreamPSDDataSet] = useState({
    data: {
      labels: upstreamPSDData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamPSDData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });
  const [downstreamPSDDataSet, setDownstreamPSDDataSet] = useState({
    data: {
      labels: downstreamPSDData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamPSDData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  // Noise
  const [upstreamNoiseStatus, setUpstreamNoiseStatus] = useState("loading");
  const [upstreamNoiseData, setUpstreamNoiseData] = useState(emptyValues);
  const [upstreamNoiseMaxPowerDb, setUpstreamNoiseMaxPowerDb] = useState(null);
  const [downstreamNoiseStatus, setDownstreamNoiseStatus] = useState("loading");
  const [downstreamNoiseData, setDownstreamNoiseData] = useState(emptyValues);
  const [downstreamNoiseMaxPowerDb, setDownstreamNoiseMaxPowerDb] =
    useState(null);
  const [upstreamNoiseDataSet, setUpstreamNoiseDataSet] = useState({
    data: {
      labels: upstreamNoiseData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: upstreamNoiseData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });
  const [downstreamNoiseDataSet, setDownstreamNoiseDataSet] = useState({
    data: {
      labels: downstreamNoiseData.map((item) => item.FrequencyMHz),
      datasets: [
        {
          borderColor: "rgb(116, 209, 34)",
          pointRadius: 0,
          fill: true,
          data: downstreamNoiseData.map((item) => item.powerDBs),
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
        },
      },
      scales: {
        y: {
          title: {
            display: true,
            text: "dBmV (1 Hz)",
            min: -120,
            max: -20,
          },
        },
        x: {
          title: {
            display: true,
            text: "MHz",
          },
          ticks: {
            callback: function (value) {
              return parseFloat(value).toFixed(0);
            },
          },
        },
      },
    },
  });

  const handleCurrentChanged = (e) => {
    const { props, state } = e;

    setCurrent(state.value);
  };

  const handleT0Changed = (e) => {
    const { props, state } = e;

    setT0(state.value);
  };

  const handleT4Changed = (e) => {
    const { props, state } = e;

    setT4(state.value);
  };

  return (
    <>
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </DomNavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>
                    <DomNavLink
                      to={{
                        pathname: "/admin/endpoints/list",
                      }}
                    >
                      Endpoints
                    </DomNavLink>
                    <i>&lt;{params}&gt;</i>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Spectrum Analysis</BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <Row>
                  <Col>
                    <FormGroup>
                      <label>Name</label>
                      <p>{details ? details.name : ""}</p>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup>
                      <label>Model Type</label>
                      <p>{details ? details.modelType : ""}</p>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup>
                      <label>Configured Port</label>
                      <p>{details ? details.configuredPort : ""}</p>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup>
                      <label>GAM Name</label>
                      <p>{details ? details.deviceName : ""}</p>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Row>
                <Col>
                  <FormGroup
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        marginBottom: "8px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "12px",
                          background:
                            "linear-gradient(to right, rgb(76, 212, 88), rgb(212, 203, 76))",
                          marginRight: "4px",
                        }}
                      ></div>
                      Current
                    </div>
                    <Switch
                      name="current"
                      offColor="success"
                      offText={<i className="nc-icon nc-simple-remove" />}
                      onColor="success"
                      onText={<i className="nc-icon nc-check-2" />}
                      onChange={handleCurrentChanged}
                      value={current}
                      disabled={loadInProgress()}
                    />
                  </FormGroup>
                </Col>

                {details && details.activeT0 ? (
                  <>
                    <Col>
                      <FormGroup
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        {" "}
                        <div
                          style={{
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "rgb(0, 0, 0)",
                              marginRight: "4px",
                            }}
                          ></div>
                          T0
                        </div>
                        <Switch
                          name="compare"
                          offColor="success"
                          offText={<i className="nc-icon nc-simple-remove" />}
                          onColor="success"
                          onText={<i className="nc-icon nc-check-2" />}
                          onChange={handleT0Changed}
                          value={t0}
                          disabled={
                            loadInProgress() ||
                            (details ? !details.activeT0 : false)
                          }
                        />
                      </FormGroup>
                    </Col>
                  </>
                ) : (
                  ""
                )}

                {details && details.activeT1T4 ? (
                  <Col>
                    <FormGroup
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <>
                        {" "}
                        <div
                          style={{
                            marginBottom: "8px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor: "rgb(72, 93, 218)",
                              marginRight: "4px",
                            }}
                          ></div>
                          T1..T4
                        </div>
                        <Switch
                          name="compare"
                          offColor="success"
                          offText={<i className="nc-icon nc-simple-remove" />}
                          onColor="success"
                          onText={<i className="nc-icon nc-check-2" />}
                          onChange={handleT4Changed}
                          value={t4}
                          disabled={loadInProgress()}
                        />
                      </>
                    </FormGroup>
                  </Col>
                ) : (
                  ""
                )}

                <Col>
                  <Button
                    onClick={() => refresh()}
                    disabled={loadInProgress()}
                    className="btn-round"
                    color="info"
                    type="submit"
                  >
                    Refresh
                  </Button>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        <Row>
          <Col>
            <Card>
              <CardBody>
                <FormGroup>
                  <label
                    style={{
                      color: "#333",
                      fontWeight: "600",
                      fontSize: "18px",
                    }}
                  >
                    AGC dB UPSTREAM
                  </label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: "4px",
                    }}
                  >
                    {upstreamAgcDB.map((item) => (
                      <span
                        key={item.tier}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          backgroundColor: "#f5f5f5",
                          border: "1px solid #e0e0e0",
                          borderRadius: "6px",
                          padding: "4px 10px",
                          fontSize: "13px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: getAgcColor(item.tier),
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        {item.label}: {item.value !== null ? item.value : "-"}
                      </span>
                    ))}
                  </div>
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Card>
              <CardBody>
                <FormGroup>
                  <label
                    style={{
                      color: "#333",
                      fontWeight: "600",
                      fontSize: "18px",
                    }}
                  >
                    AGC dB DOWNSTREAM
                  </label>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      marginTop: "4px",
                    }}
                  >
                    {downstreamAgcDB.map((item) => (
                      <span
                        key={item.tier}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          backgroundColor: "#f5f5f5",
                          border: "1px solid #e0e0e0",
                          borderRadius: "6px",
                          padding: "4px 10px",
                          fontSize: "13px",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        <span
                          style={{
                            width: "10px",
                            height: "10px",
                            borderRadius: "50%",
                            background: getAgcColor(item.tier),
                            display: "inline-block",
                            flexShrink: 0,
                          }}
                        />
                        {item.label}: {item.value !== null ? item.value : "-"}
                      </span>
                    ))}
                  </div>
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <LoadingOverlay
              active={upstreamSNRStatus !== "ok"}
              spinner={upstreamSNRStatus === "loading"}
              text={showOverlayMessage(
                upstreamSNRStatus,
                "upstream",
                "snrProbe",
              )}
            >
              <ChartComponent
                dataset={upstreamSNRDataSet}
                title="Upstream SNR"
                maxPowerDb={
                  Number.isFinite(Number(upstreamSNRMaxPowerDb))
                    ? `${upstreamSNRMaxPowerDb} dB`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
          <Col md="6">
            <LoadingOverlay
              active={downstreamSNRStatus !== "ok"}
              spinner={downstreamSNRStatus === "loading"}
              text={showOverlayMessage(
                downstreamSNRStatus,
                "downstream",
                "snrProbe",
              )}
            >
              <ChartComponent
                dataset={downstreamSNRDataSet}
                title="Downstream SNR"
                maxPowerDb={
                  Number.isFinite(Number(downstreamSNRMaxPowerDb))
                    ? `${downstreamSNRMaxPowerDb} dB`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <LoadingOverlay
              active={upstreamPSDStatus !== "ok"}
              spinner={upstreamPSDStatus === "loading"}
              text={showOverlayMessage(upstreamPSDStatus, "upstream", "psdRx")}
            >
              <ChartComponent
                dataset={upstreamPSDDataSet}
                title="Upstream PSD"
                maxPowerDb={
                  Number.isFinite(Number(upstreamPSDMaxPowerDb))
                    ? `${upstreamPSDMaxPowerDb + PSD_NOISE_OFFSET} dBmV (1 Hz)`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
          <Col md="6">
            <LoadingOverlay
              active={downstreamPSDStatus !== "ok"}
              spinner={downstreamPSDStatus === "loading"}
              text={showOverlayMessage(
                downstreamPSDStatus,
                "downstream",
                "psdRx",
              )}
            >
              <ChartComponent
                dataset={downstreamPSDDataSet}
                title="Downstream PSD"
                maxPowerDb={
                  Number.isFinite(Number(downstreamPSDMaxPowerDb))
                    ? `${
                        downstreamPSDMaxPowerDb + PSD_NOISE_OFFSET
                      } dBmV (1 Hz)`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
        </Row>
        <Row>
          <Col md="6">
            <LoadingOverlay
              active={upstreamNoiseStatus !== "ok"}
              spinner={upstreamNoiseStatus === "loading"}
              text={showOverlayMessage(
                upstreamNoiseStatus,
                "upstream",
                "noise",
              )}
            >
              <ChartComponent
                dataset={upstreamNoiseDataSet}
                title="Upstream Noise"
                maxPowerDb={
                  Number.isFinite(Number(upstreamNoiseMaxPowerDb))
                    ? `${
                        upstreamNoiseMaxPowerDb + PSD_NOISE_OFFSET
                      } dBmV (1 Hz)`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
          <Col md="6">
            <LoadingOverlay
              active={downstreamNoiseStatus !== "ok"}
              spinner={downstreamNoiseStatus === "loading"}
              text={showOverlayMessage(
                downstreamNoiseStatus,
                "downstream",
                "noise",
              )}
            >
              <ChartComponent
                dataset={downstreamNoiseDataSet}
                title="Downstream Noise"
                maxPowerDb={
                  Number.isFinite(Number(downstreamNoiseMaxPowerDb))
                    ? `${
                        downstreamNoiseMaxPowerDb + PSD_NOISE_OFFSET
                      } dBmV (1 Hz)`
                    : ""
                }
              />
            </LoadingOverlay>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default EndpointSpectrum;
